import { parse, stringify } from 'svgson';
import ColorsList from './colorsList';
import { rgb2Array, rgb2Hex, rgbp2Hex, rgbpa2Hex, rgbaConversion } from './javascript';
import Regex from './regex';
import APP_CONSTANTS from './helper';

const PATH_C_ATTR = new RegExp('C', 'g');
const WIDTH_STYLE_REGEX = new RegExp(/width:.*?;/, 'g');
const HEIGHT_STYLE_REGEX = new RegExp(/height:.*?;/, 'g');

export const processIfSVG = async (url, styles, uiType, isStaticTableBlock=false, isPixels = true, element) => {
  return await new Promise(resolve => {
    let request = new XMLHttpRequest();
    request.open('GET', correctBaseUrl(url), true);
    request.send(null);
    request.onreadystatechange = async () => {
      if (request.readyState === 4 && request.status === 200) {
        let svg = String(request.responseText).trim();
        const isSvg = ['<?xml', '<svg'].some(data => svg.startsWith(data));
        if (isSvg) {
          const { width, height } = styles;
          parse(svg).then(res => {
            res['attributes']['width'] = width;
            res['attributes']['height'] = height;
            let widthInPixels;
            if (isStaticTableBlock && uiType ==='line' && !res['attributes']['viewBox']) {
              if(width.includes('%')){
                const widthValue =parseFloat(width.replace('%', ''));
                widthInPixels = `${(widthValue / 100) * element.offsetWidth}px`;
              }else if(width.includes('px')){
                widthInPixels = width;
              }
              res['attributes']['viewBox'] = `0 0 ${parseFloat(widthInPixels)} ${parseFloat(height)}`;
            }
            const isLine_double = ['line', 'doubleLine'].includes(uiType) && isStaticTableBlock;
            const isDoubleLine =  uiType === 'doubleLine' && isStaticTableBlock;
            let hasStylesAlready = false;
            let suffix = isPixels ? 'px' : '%';
            if (res['attributes']['style']) {
              hasStylesAlready = true;
              res['attributes']['style'] = (res['attributes']['style'] || '').replaceAll(WIDTH_STYLE_REGEX, `width:${`${width}`.replaceAll(isLine_double? '%' : suffix, '')}${isLine_double? '%' : suffix};`);
              res['attributes']['style'] = (res['attributes']['style'] || '').replaceAll(HEIGHT_STYLE_REGEX, `height:${`${height}`.replaceAll(suffix, '')}${suffix};`);
            } else {
              res['attributes']['style'] = `width:${`${isDoubleLine? '100': width}`.replaceAll(isLine_double? '%' : suffix, '')}${isLine_double? '%' : suffix};height:${`${height}`.replaceAll(suffix, '')}${suffix};`;
            }
            return stringify(res);
          }).then(svgString => resolve(svgString));
        } else {
          resolve(url);
        }
      }
    };
  });
};

export const parseSvgData = (svgString, styles = {}) => {
  const { height, width } = styles;
  return new Promise((resolve, reject) => {
    let svg = String(svgString).trim();
    let rgbString = svg && svg.split('rgb(');
    rgbString.shift();
    if (rgbString?.length > 0) {
      for (const color of rgbString) {
        let rgbKey = color.split(')')[0];
        let isRgbp = rgbKey.indexOf('%');
        const rgbArray = rgb2Array(rgbKey);
        rgbKey = `rgb(${rgbKey})`;
        let hex = '';
        if (rgbArray.length > 3) {
          if (isRgbp) {
            hex = rgbpa2Hex(rgbArray);
          } else {
            hex = rgbaConversion(rgbKey);
          }
        } else {
          if (isRgbp) {
            hex = rgbp2Hex(rgbArray);
          } else {
            hex = rgb2Hex(rgbArray);
          }
        }
        svg = svg.replace(rgbKey, `${hex}`);
      }
    }
    parse(svg).then(res => {
      if (width) {
        res['attributes']['width'] = width && `${parseInt(Math.round(`${width}`.replace('px', '')))}px`;
      }
      if (height) {
        res['attributes']['height'] = height && `${parseInt(Math.round(`${height}`.replace('px', '')))}px`;
      }
      let url = svgString2Url(stringify(res));
      let json = { ...res, url };
      resolve(json);
    });
  });
};

export const getSvgColors = value => {
  let fullSvg = JSON.stringify(value);
  let string = JSON.stringify(value.children);
  if (string) {
    let colors = [];
    const filters = [
      { array: string, splitType: '"fill":"', rgbSplit: ',' },
      { array: string, splitType: 'fill:', rgbSplit: ';' },
      { array: string, splitType: '"stop-color":"', rgbSplit: '}' },
      { array: string, splitType: 'stop-color:', rgbSplit: ';' },
      { array: fullSvg, splitType: '"stroke":"', rgbSplit: ',' },
      { array: fullSvg, splitType: 'stroke:', rgbSplit: ';' },
    ];

    for (const data of filters) {
      getColorObj(data, color => {
        color && colors.push(color);
      });
    }
    let result = Array.from(new Set(colors.map(data => data.oldColor)));
    result = result.map(color => {
      return {
        oldColor: color,
        newColor: colors.find(s => s.oldColor === color).newColor,
      };
    });
    result = getOpacity(value, result);

    return result;
  } else {
    return false;
  }
};

const getHexWithOpacity = (colorObj, opacity) => {
  let result = '';
  if (opacity) {
    let p = Number(opacity) * 100;
    const intValue = Math.round((p / 100) * 255); // map percent to nearest integer (0 - 255)
    const hexValue = intValue.toString(16); // get hexadecimal representation
    result = hexValue.padStart(2, '0').toUpperCase(); // format with leading 0 and upper case characters
  }
  return `${colorObj.newColor}${result}`;
};

const getOpacity = (value, colors) => {
  let svgString = stringify(value);
  let div = document.createElement('div');
  div.innerHTML = svgString;
  for (let color of colors) {
    // Get opacity from style attribute
    let targets = div.querySelectorAll(`[style*='${color.newColor}']`);
    if (targets.length > 0) {
      for (const target of targets) {
        let attributes = target.getAttribute('style');
        if (attributes.includes('opacity')) {
          attributes = attributes.split(';');
          let opacity = attributes.filter(a => a.includes('opacity'))[0];
          opacity = opacity.split('opacity:')[1];
          let hexColor = getHexWithOpacity(color, opacity);
          color.hexColor = hexColor;
        }
      }
    }
    // Get opacity from stroke attribute
    let strokes = div.querySelectorAll(`[stroke*='${color.newColor}']`);
    if (strokes.length > 0) {
      for (const stroke of strokes) {
        const opacity = stroke.getAttribute('stroke-opacity');
        let hexColor = getHexWithOpacity(color, opacity);
        color.hexColor = hexColor;
      }
    }
    // Get opacity from fill attribute
    let fills = div.querySelectorAll(`[fill*='${color.newColor}']`);
    if (fills.length > 0) {
      for (const fill of fills) {
        const opacity = fill.getAttribute('fill-opacity');
        let hexColor = getHexWithOpacity(color, opacity);
        color.hexColor = hexColor;
      }
    }
  }
  return colors;
};

const getColorObj = (data, callback) => {
  const { array, splitType, rgbSplit } = data;
  const stringColors = array && array.split(splitType);
  stringColors.shift();
  if (stringColors.length > 0) {
    stringColors.forEach(data => {
      let hex = data.split(rgbSplit)[0];
      hex = hex.includes(`'`)
        ? hex.split(`'`)[0]
        : hex.includes(`"`)
        ? hex.split(`"`)[0]
        : hex.includes(',')
        ? hex.split(',')[0]
        : hex;
      hex = hex.includes('#')
        ? hex.replace(Regex.ALPHANUMERIC_WITH_HASH, '')
        : hex.replace(Regex.ALPHABET_ONLY, '');
      if (hex.startsWith('#') || ColorsList[hex]) {
        hex = hex.slice(0, 9);
        let color = {
          oldColor: hex,
          newColor: ColorsList[hex] || hex,
        };
        color && callback(color);
      }
    });
  }
};

const svgString2Url = string => {
  let svgElement = '';
  const markup = { '&amp;quot;': "'" };
  const RE = new RegExp(Object.keys(markup).join('|'), 'gi');
  svgElement = String(string.replace(RE, matched => markup[matched]));
  const blob = new Blob([svgElement], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob); // TODO : Revoke object URL to save up memory
  return url;
};


export const updateSvgColor = async (oldColor, newColor, element, opacity) => {
  let tempDetails = JSON.parse(JSON.stringify(element.elementDetails));
  const prevUrl = `${tempDetails.value.url}`;
  let string = JSON.stringify(tempDetails.value);
  const slicedOldColor = oldColor.slice(0, 7);
  const svgColors = {
    [oldColor]: newColor,
    [slicedOldColor]: newColor
  };
  const RE = new RegExp(Object.keys(svgColors).join('|'), 'gi');
  string = string.replace(RE, matched => svgColors[matched]);
  let updatedSvg = updateSvgColors(string, newColor, opacity);
  const url = svgString2Url(updatedSvg); // convert the svg string into blob url
  let parsedString = await parse(updatedSvg);
  parsedString = { ...parsedString, url };
  tempDetails.value = parsedString;
  let colors = getSvgColors(tempDetails.value);
  if (colors) {
    tempDetails.svgColors = colors;
  }
  if (prevUrl) {
    setTimeout(() => {
      URL.revokeObjectURL(prevUrl);
    }, 10000);
  }
  return {
    element: Object.assign({}, element, {
      elementDetails: tempDetails,
    }),
    parsedString,
    svgString: updatedSvg
  };
};

export const updateSvgLineWeight = (lineWeight, element, STYLE_KEY = 'styles') => {
  let tempDetails = JSON.parse(JSON.stringify(element.elementDetails));
  const prevUrl = `${tempDetails.value.url}`;
  let lineWidth = tempDetails[STYLE_KEY].width.replace(/[^.\d]/g, '');
  const isStaticTableBlock = tempDetails.staticTableBlock;
  let viewBoxValues;
  if(tempDetails[STYLE_KEY].viewBox){
    const viewBox = tempDetails[STYLE_KEY].viewBox;
    viewBoxValues = viewBox.split(' ');
  }
  // Update svg image data width & height attributes
  const targetSvgElement = document.querySelectorAll(`[data-uuid='${element.uuid}']`);
  if (targetSvgElement && targetSvgElement.length > 0) {
    if (tempDetails.uiType === 'doubleLine') {
      let lineHeight = tempDetails.value.children[0].children[0].attributes['height'];
      let strokeWidth = tempDetails.value.children[0].children[0].attributes['stroke-width'];
      let lineDistance = lineHeight - strokeWidth;
      lineHeight = lineWeight + lineDistance;
      targetSvgElement[0].setAttribute('data-viewheight', lineWeight + lineHeight);
    } else {
      targetSvgElement[0].setAttribute('data-viewheight', lineWeight);
    }
    if (targetSvgElement[0].getAttribute('data-viewport')) {
      lineWidth = Number(targetSvgElement[0].getAttribute('data-viewport'));
    }
  }

  if (tempDetails.uiType === 'doubleLine') {
    let lineHeight = tempDetails.value.children[0].children[0].attributes['height'];
    let strokeWidth = tempDetails.value.children[0].children[0].attributes['stroke-width'];
    let lineDistance = lineHeight - strokeWidth;
    lineHeight = lineWeight + lineDistance;
    tempDetails.value.attributes.width = lineWidth + 'px';
    tempDetails.value.attributes.height = lineWeight + lineHeight + 'px';
    if (tempDetails.value.attributes.style) {
      tempDetails.value.attributes.style = tempDetails.value.attributes.style.replaceAll(HEIGHT_STYLE_REGEX, `height:${lineWeight + lineHeight}px;`)
    }
    tempDetails.value.children[0].children[0].attributes['x'] = String(-(lineWeight / 2));
    tempDetails.value.children[0].children[0].attributes['y'] = String(lineWeight / 2);
    tempDetails.value.children[0].children[0].attributes['stroke-width'] = lineWeight;
    tempDetails.value.children[0].children[0].attributes['height'] = lineHeight;
    tempDetails[STYLE_KEY].width = lineWidth + 'px'; //svg width
    tempDetails[STYLE_KEY].height = lineWeight + lineHeight + 'px'; //svg height
  } else {
    let x2 = (isStaticTableBlock ? Number(viewBoxValues[2]) :lineWidth) / lineWeight;
    x2 = x2 - tempDetails.value.children[0].children[0].attributes['x1'];
    let widthSufix= isStaticTableBlock ?  '%' : 'px'
    tempDetails[STYLE_KEY].width = lineWidth + widthSufix;
    tempDetails[STYLE_KEY].height = lineWeight + 'px';
    tempDetails.value.attributes.width = lineWidth + widthSufix;
    tempDetails.value.attributes.height = lineWeight + 'px';
    if (tempDetails.value.attributes.style) {
      tempDetails.value.attributes.style = tempDetails.value.attributes.style.replaceAll(HEIGHT_STYLE_REGEX, `height:${lineWeight}px;`)
    }
    if(tempDetails.value.attributes?.viewBox){
      viewBoxValues[viewBoxValues.length - 1] = lineWeight.toString();
      const val = viewBoxValues.join(' ');
      tempDetails.value.attributes.viewBox = val;
    }
    tempDetails.value.children[0].children[0].attributes['x2'] = String(x2);
    tempDetails.value.children[0].attributes.transform =
      'scale(' + lineWeight + ') translate(0, 0.5)';
  }

  let parsedString = JSON.parse(JSON.stringify(tempDetails.value));
  const stringSVG = stringify(tempDetails.value);
  const url = svgString2Url(stringSVG); // convert the svg string into blob url
  parsedString = { ...parsedString, url };
  tempDetails.value = parsedString;

  if (prevUrl) {
    setTimeout(() => {
      URL.revokeObjectURL(prevUrl);
    }, 10000);
  }

  return {
    element: Object.assign({}, element, {
      elementDetails: tempDetails,
    }),
    parsedString,
    stringSVG
  };
};

export const updateLineStyles = (lineStyle, element, STYLE_KEY = 'styles') => {
  let tempDetails = JSON.parse(JSON.stringify(element.elementDetails));
  const prevUrl = `${tempDetails.value.url}`;
  let lineWidth = tempDetails[STYLE_KEY].width.replace(/[^.\d]/g, '');
  let lineWeight = tempDetails[STYLE_KEY].height.replace(/[^.\d]/g, '');
  const isStaticTableBlock = tempDetails.staticTableBlock;
  const viewBox = tempDetails[STYLE_KEY].viewBox;
  let viewBoxValues = viewBox?.split(' ');  
  let x2 =  (isStaticTableBlock ? Number(viewBoxValues[2]):lineWidth) / (isStaticTableBlock ? Number(viewBoxValues[3]):lineWeight);
  x2 = x2 - tempDetails.value.children[0].children[0].attributes['x1'];

  switch (lineStyle) {
    case 'square':
      tempDetails.value.attributes['data-linetype'] = 'square';
      tempDetails.value.children[0].children[0].attributes['x2'] = String(x2);
      tempDetails.value.children[0].children[0].attributes['stroke-linecap'] = 'square';
      tempDetails.value.children[0].children[0].attributes['stroke-dasharray'] = '0';
      break;
    case 'round':
      tempDetails.value.attributes['data-linetype'] = 'round';
      tempDetails.value.children[0].children[0].attributes['x2'] = String(x2);
      tempDetails.value.children[0].children[0].attributes['stroke-linecap'] = 'round';
      tempDetails.value.children[0].children[0].attributes['stroke-dasharray'] = '0';
      break;
    case 'dash':
      tempDetails.value.attributes['data-linetype'] = 'dash';
      tempDetails.value.children[0].children[0].attributes['x2'] = String(x2);
      tempDetails.value.children[0].children[0].attributes['stroke-linecap'] = 'round';
      tempDetails.value.children[0].children[0].attributes['stroke-dasharray'] = '3,2';
      break;
    case 'dot':
      tempDetails.value.attributes['data-linetype'] = 'dot';
      tempDetails.value.children[0].children[0].attributes['x2'] = String(x2);
      tempDetails.value.children[0].children[0].attributes['stroke-linecap'] = 'round';
      tempDetails.value.children[0].children[0].attributes['stroke-dasharray'] = '0,2';
      break;
    default:
      break;
  }
  let parsedString = JSON.parse(JSON.stringify(tempDetails.value));
  const stringSVG = stringify(tempDetails.value);
  const url = svgString2Url(stringSVG); // convert the svg string into blob url
  parsedString = { ...parsedString, url };
  tempDetails.value = parsedString;

  if (prevUrl) {
    setTimeout(() => {
      URL.revokeObjectURL(prevUrl);
    }, 10000);
  }

  return {
    element: Object.assign({}, element, {
      elementDetails: tempDetails,
    }),
    parsedString,
    stringSVG
  };
};

export const updateDoubleLineDistance = (lineDistance, element, STYLE_KEY = 'styles') => {
  let tempDetails = JSON.parse(JSON.stringify(element.elementDetails));
  const prevUrl = `${tempDetails.value.url}`;

  if (prevUrl) {
    setTimeout(() => {
      URL.revokeObjectURL(prevUrl);
    }, 10000);
  }

  let lineWeight = tempDetails.value.children[0].children[0].attributes['stroke-width'];
  let lineHeight = +lineWeight + lineDistance;
  let svgHeight = +lineWeight + lineHeight;

  // Update svg image data width & height attributes
  let lineWidth = tempDetails[STYLE_KEY].width.replace(/[^.\d]/g, '');
  const targetSvgElement = document.querySelectorAll(`[data-uuid='${element.uuid}']`);
  if (targetSvgElement && targetSvgElement.length > 0) {
    if (tempDetails.uiType === 'doubleLine') {
      targetSvgElement[0].setAttribute('data-viewheight', svgHeight);
    }
    if (targetSvgElement[0].getAttribute('data-viewport')) {
      lineWidth = Number(targetSvgElement[0].getAttribute('data-viewport'));
    }
  }

  tempDetails.value.attributes.width = lineWidth + 'px';
  tempDetails.value.attributes.height = svgHeight + 'px';
  if (tempDetails.value.attributes.style) {
    tempDetails.value.attributes.style = tempDetails.value.attributes.style.replaceAll(HEIGHT_STYLE_REGEX, `height:${svgHeight}px;`)
  }
  tempDetails.value.children[0].children[0].attributes['height'] = lineHeight;
  tempDetails[STYLE_KEY].width = lineWidth + 'px'; //svg width
  tempDetails[STYLE_KEY].height = svgHeight + 'px'; //svg height

  let parsedString = JSON.parse(JSON.stringify(tempDetails.value));
  const stringSVG = stringify(tempDetails.value);
  const url = svgString2Url(stringSVG); // convert the svg string into blob url
  parsedString = { ...parsedString, url };
  tempDetails.value = parsedString;

  return {
    element: Object.assign({}, element, {
      elementDetails: tempDetails,
    }),
    parsedString,
    stringSVG
  };
};

export const updateBorderRectangle = (borderWidth, borderRadius, element, isPixels = true) => {
  let tempDetails = JSON.parse(JSON.stringify(element.elementDetails));
  const prevUrl = `${tempDetails.value.url}`;

  if (prevUrl) {
    setTimeout(() => {
      URL.revokeObjectURL(prevUrl);
    }, 10000);
  }

  const svgWidth = isPixels
    ? Number(tempDetails.value.attributes.width.replace('px', ''))
    : Number(tempDetails.value.attributes.viewBox.split(" ")[2]);
  const svgHeight = isPixels
    ? Number(tempDetails.value.attributes.height.replace('px', ''))
    : Number(tempDetails.value.attributes.viewBox.split(" ")[3]);

  const gTag = tempDetails.value.children[1].children[0];
  const isRoundedRect = (gTag.children[0].attributes.d.match(PATH_C_ATTR) || []).length > 0 || borderRadius !== 0;
  const isBordered = gTag.children.length > 1;
  let borderChild = {
    "name": "path",
    "type": "element",
    "value": "",
    "attributes": {
      "style": "stroke:none;fill-rule:nonzero;fill:#000000;fill-opacity:1;",
      "d": ""
    },
    "children": []
  };
//  console.log({ isRoundedRect, isBordered, borderRadius, borderWidth });
  if (isBordered) {
    borderChild = { ...gTag.children[gTag.children.length - 1] };
  }
  tempDetails.value.attributes.viewBox = `0 0 ${svgWidth} ${svgHeight}`;
  tempDetails.value.children[0].children[0].children[0].attributes.d = `M 0 0 L ${svgWidth} 0 L ${svgWidth} ${svgHeight} L 0 ${svgHeight} Z M 0 0`;
  if (borderRadius && borderRadius !== 0) {
    const minValue = Math.min(svgWidth, svgHeight);
    if (borderRadius > minValue) {
      borderRadius = minValue;
    }
    let innerWidth = svgWidth - borderRadius;
    let innerHeight = svgHeight - borderRadius;
    let diff = borderRadius * 0.52;
//    tempDetails.value.children[1].children[0].children[0].attributes.d = `M ${innerWidth} ${svgHeight} L ${borderRadius} ${svgHeight} C ${borderRadius - diff} ${svgHeight} 0 ${innerHeight + diff} 0 ${innerHeight} L 0 ${borderRadius} C 0 ${borderRadius - diff} ${borderRadius - diff} 0 ${borderRadius} 0 L ${innerWidth} 0 C ${innerWidth + diff} 0 ${svgWidth} ${borderRadius - diff} ${svgWidth} ${borderRadius} L ${svgWidth} ${innerHeight} C ${svgWidth} ${innerHeight + diff} ${innerWidth + diff} ${svgHeight} ${innerWidth} ${svgHeight} Z M ${innerWidth} ${svgHeight}`;
    const factor = borderRadius * 0.556;
    tempDetails.value.children[1].children[0].children[0].attributes.d =
            `M ${innerWidth} ${svgHeight}` +
            `L ${borderRadius} ${svgHeight}` +
            `C ${borderRadius - factor} ${svgHeight} 0 ${innerHeight + factor} 0 ${innerHeight}` +
            `L 0 ${borderRadius}` +
            `C 0 ${borderRadius - factor} ${borderRadius - factor} 0 ${borderRadius} 0` +
            `L ${innerWidth} 0` +
            `C ${innerWidth + factor} 0 ${svgWidth} ${borderRadius - factor} ${svgWidth} ${borderRadius}` +
            `L ${svgWidth} ${innerHeight}` +
            `C ${svgWidth} ${innerHeight + factor} ${innerWidth + factor} ${svgHeight} ${innerWidth} ${svgHeight}` +
            `Z M ${innerWidth} ${svgHeight}`;
  } else {
    tempDetails.value.children[1].children[0].children[0].attributes.d = `M 0 0 L ${svgWidth} 0 L ${svgWidth} ${svgHeight} L 0 ${svgHeight}  Z M 0 0`;
  }
  if (borderWidth && borderWidth !== 0) {
    const minValue = svgWidth > svgHeight ? svgHeight : svgWidth;
    if (borderWidth > minValue / 2) {
      borderWidth = minValue / 2;
    }
    if (isRoundedRect) {
      if (borderWidth > borderRadius) {
        if (borderRadius > minValue / 4) {
          borderWidth = minValue / 4;
        } else {
          borderWidth = borderRadius;
        }
      }
      let innerWidth = svgWidth - borderRadius;
      let innerHeight = svgHeight - borderRadius;
      let diff = borderRadius * 0.52;
      let factor = borderRadius * 0.556;
      // borderChild.attributes.d = `M ${innerWidth} ${svgHeight} L ${borderRadius} ${svgHeight} C ${borderRadius - diff} ${svgHeight} 0 ${innerHeight + diff} 0 ${innerHeight} L 0 ${borderRadius} C 0 ${borderRadius - diff} ${borderRadius - diff} 0 ${borderRadius} 0 L ${innerWidth} 0 C ${innerWidth + diff} 0 ${svgWidth} ${borderRadius - diff} ${svgWidth} ${borderRadius} L ${svgWidth} ${innerHeight} C ${svgWidth} ${innerHeight + diff} ${innerWidth + diff} ${svgHeight} ${innerWidth} ${svgHeight} Z `;
      borderChild.attributes.d = `M ${innerWidth} ${svgHeight}` +
                  `L ${borderRadius} ${svgHeight}` +
                  `C ${borderRadius - factor} ${svgHeight} 0 ${innerHeight + factor} 0 ${innerHeight}` +
                  `L 0 ${borderRadius}` +
                  `C 0 ${borderRadius - factor} ${borderRadius - factor} 0 ${borderRadius} 0` +
                  `L ${innerWidth} 0` +
                  `C ${innerWidth + factor} 0 ${svgWidth} ${borderRadius - factor} ${svgWidth} ${borderRadius}` +
                  `L ${svgWidth} ${innerHeight}` +
                  `C ${svgWidth} ${innerHeight + factor} ${innerWidth + factor} ${svgHeight} ${innerWidth} ${svgHeight}` +
                  `Z`;

      const left = borderWidth;
      const right = svgWidth - borderWidth;
      const top = borderWidth;
      const bottom = svgHeight - borderWidth;

      const innerBorderRadius = borderRadius - (borderWidth);

      let innerLeft = borderWidth + innerBorderRadius;
      let innerRight = svgWidth - borderWidth - innerBorderRadius;
      let innerTop = borderWidth + innerBorderRadius;
      let innerBottom = svgHeight - borderWidth - innerBorderRadius;

//      let innerBorderRadius = borderRadius + borderWidth;

      /*const borderRectWidth = svgWidth - borderWidth;
      const borderRectHeight = svgHeight - borderWidth;
      let innerBorderRadius = borderRadius + borderWidth;
      innerWidth = svgWidth - innerBorderRadius;
      innerHeight = svgHeight - innerBorderRadius;
      diff = innerBorderRadius * 0.52;
      factor = innerBorderRadius * 0.556;
      console.log({ innerWidth, innerHeight, borderRectWidth, borderRectHeight, innerBorderRadius, factor });
      // TODO : Continue from here
      borderChild.attributes.d = `${borderChild.attributes.d}`
//        + `M ${innerWidth} ${borderRectHeight} C ${innerWidth + diff} ${borderRectHeight} ${borderRectWidth} ${innerHeight + diff} ${borderRectWidth} ${innerHeight} L ${borderRectWidth} ${innerBorderRadius} C ${borderRectWidth} ${innerBorderRadius - diff} ${innerWidth + diff} ${borderWidth} ${innerWidth} ${borderWidth} L ${innerBorderRadius} ${borderWidth} C ${innerBorderRadius - diff} ${borderWidth} ${borderWidth} ${innerBorderRadius - diff} ${borderWidth} ${innerBorderRadius} L ${borderWidth} ${innerHeight} C ${borderWidth} ${innerHeight + diff} ${innerBorderRadius - diff} ${borderRectHeight} ${innerBorderRadius} ${borderRectHeight} L ${innerWidth} ${borderRectHeight} Z M ${innerWidth} ${borderRectHeight} `;
          +  `M ${innerWidth} ${borderRectHeight}`
          +  `C ${innerWidth + factor} ${borderRectHeight} ${borderRectWidth} ${innerHeight + factor} ${borderRectWidth} ${innerHeight} `
          +  `L ${borderRectWidth} ${innerBorderRadius} `
          +  `C ${borderRectWidth} ${innerBorderRadius - factor} ${innerWidth + factor} ${borderWidth} ${innerWidth} ${borderWidth} `
          +  `L ${innerBorderRadius} ${borderWidth} `
          +  `C ${innerBorderRadius - factor} ${borderWidth} ${borderWidth} ${innerBorderRadius - factor} ${borderWidth} ${innerBorderRadius} `
          +  `L ${borderWidth} ${innerHeight} `
          +  `C ${borderWidth} ${innerHeight + factor} ${innerBorderRadius - factor} ${borderRectHeight} ${innerBorderRadius} ${borderRectHeight} `
          +  `L ${innerWidth} ${borderRectHeight} `
          +  `Z M ${innerWidth} ${borderRectHeight} `;*/

      factor = innerBorderRadius * 0.556;
      // TODO : Continue from here
      borderChild.attributes.d = `${borderChild.attributes.d}`
            +  `M ${innerRight} ${bottom}`
            +  `C ${innerRight + factor} ${bottom} ${right} ${innerBottom + factor} ${right} ${innerBottom} `
            +  `L ${right} ${innerTop} `
            +  `C ${right} ${innerTop - factor} ${innerRight + factor} ${top} ${innerRight} ${top} `
            +  `L ${innerLeft} ${top} `
            +  `C ${innerLeft - factor} ${top} ${left} ${innerTop - factor} ${left} ${innerTop} `
            +  `L ${left} ${innerBottom} `
            +  `C ${left} ${innerBottom + factor} ${innerLeft - factor} ${bottom} ${innerLeft} ${bottom} `
            +  `L ${innerRight} ${bottom} `
            +  `Z M ${innerRight} ${bottom} `;
    } else {
      borderChild.attributes.d = `M 0 0 L 0 ${svgHeight} L ${svgWidth} ${svgHeight} L ${svgWidth} 0 Z M 0 0 M ${svgWidth - borderWidth} ${svgHeight - borderWidth} L ${borderWidth} ${svgHeight - borderWidth} L ${borderWidth} ${borderWidth} L ${svgWidth - borderWidth} ${borderWidth} Z M ${svgWidth - borderWidth} ${svgHeight - borderWidth} `;
    }
    if (!isBordered) {
      tempDetails.value.children[1].children[0].children = [...gTag.children, borderChild];
    } else {
      tempDetails.value.children[1].children[0].children[gTag.children.length - 1].attributes.d = borderChild.attributes.d;
    }
  } else {
    tempDetails.value.children[1].children[0].children = [ tempDetails.value.children[1].children[0].children[0] ];
  }
  tempDetails.value.attributes['data-border-width'] = borderWidth;
  tempDetails.value.attributes['data-border-radius'] = borderRadius;

  let parsedString = JSON.parse(JSON.stringify(tempDetails.value));
  let stringSVG = stringify(tempDetails.value);
  const url = svgString2Url(stringSVG); // convert the svg string into blob url
  parsedString = { ...parsedString, url };
  tempDetails.value = parsedString;

  return {
    element: Object.assign({}, element, {
      elementDetails: tempDetails,
    }),
    parsedString,
    stringSVG
  };
};

export const updateBorderCircle = (borderWidth, element, isPixels = true) => {
  let tempDetails = JSON.parse(JSON.stringify(element.elementDetails));

  let { width, height } = tempDetails.value.attributes;

  if (!isPixels) {
    width = tempDetails.value.attributes.viewBox.split(" ")[2];
    height = tempDetails.value.attributes.viewBox.split(" ")[3];
  }

  const circleSize = {
    width: parseInt(width, 10) / 2,
    height: parseInt(height, 10) / 2,
  };

  tempDetails.value.attributes.viewBox = `0 0 ${width.replace('px', '')} ${height.replace('px','')}`;
  tempDetails.value.children[0].attributes['stroke-width'] = borderWidth;

  if(borderWidth > 0) {
    if(!tempDetails.value.children[0].attributes['stroke'] || tempDetails.value.children[0].attributes['stroke'] === 'none')
    tempDetails.value.children[0].attributes['stroke'] = '#000000';
  } else {
    tempDetails.value.children[0].attributes['stroke'] = 'none';
  }

  tempDetails.value.children[0].attributes['cx'] = circleSize.width;
  tempDetails.value.children[0].attributes['cy'] = circleSize.height;
  tempDetails.value.children[0].attributes['r'] = circleSize.width-(borderWidth/2);

  
  let parsedString = JSON.parse(JSON.stringify(tempDetails.value));
  let stringSVG = stringify(tempDetails.value);
  const url = svgString2Url(stringSVG);
  parsedString = { ...parsedString, url };
  tempDetails.value = parsedString;

  return {
    element: Object.assign({}, element, {
      elementDetails: tempDetails,
    }),
    parsedString,
    stringSVG,
  };
}

const updateSvgColors = (svgString, color, transparency) => {
  let parsedString = JSON.parse(svgString);
  const svgData = stringify(parsedString);
  let div = document.createElement('div');
  div.innerHTML = svgData;
  let newColor = `${color}`;
  if (transparency !== undefined) {
    newColor = newColor.slice(0, 7);
    // Update colors in style attribute
    let targets = div.querySelectorAll(`[style*='${newColor}']`);
    if (targets.length > 0) {
      for (const target of targets) {
        let text = target.getAttribute('style');
        if (text) {
          let attributes = text.split(';');
          let colorIndex = attributes.findIndex(a => a.includes(newColor));
          let colorProperty = attributes[colorIndex].split(`:`)[0];
          let opacity = `${colorProperty}-opacity:${transparency}`;
          const newAttribute = `${colorProperty}:${newColor}`;
          let exist = attributes.findIndex(a => a.startsWith(`${colorProperty}-opacity:`));
          if (exist !== -1) {
            delete attributes[exist];
          }
          attributes.push(opacity);
          attributes[colorIndex] = newAttribute;
          target.setAttribute('style', attributes.join(';'));
        }
      }
    }
    // Update colors in strokes attribute
    let strokes = div.querySelectorAll(`[stroke*='${newColor}']`);
    if (strokes.length > 0) {
      for (const stroke of strokes) {
        stroke.setAttribute('stroke-opacity', transparency);
        stroke.setAttribute('stroke', newColor);
      }
    }
    // Update colors in fill attribute
    let fills = div.querySelectorAll(`[fill*='${newColor}']`);
    if (fills.length > 0) {
      for (const fill of fills) {
        fill.setAttribute('fill-opacity', transparency);
        fill.setAttribute('fill', newColor);
      }
    }
  }
  return div.innerHTML;
};

export const updateSvgValues = (updatedSVGStyles, element, styleKey = 'styles') => {
  let tempDetails = JSON.parse(JSON.stringify(element.elementDetails));
  const prevUrl = `${tempDetails.value.url}`;
  let lineWidth = Number(updatedSVGStyles.width);
  let lineWeight = Number(updatedSVGStyles.height);

  switch (tempDetails.uiType) {
    case 'line':
      let x2 = lineWidth / lineWeight;
      x2 = x2 - tempDetails.value.children[0].children[0].attributes['x1'];
      tempDetails[styleKey].width = lineWidth + 'px';
      tempDetails[styleKey].height = lineWeight + 'px';
      tempDetails.value.attributes.width = lineWidth + 'px';
      tempDetails.value.attributes.height = lineWeight + 'px';
      tempDetails.value.children[0].children[0].attributes['x2'] = String(x2);
      tempDetails.value.children[0].attributes.transform =
        'scale(' + lineWeight + ') translate(0, 0.5)';
      if(tempDetails.value.attributes?.viewBox){
        tempDetails.value.attributes.viewBox = `0 0 ${lineWidth} ${lineWeight}`;
      }  
      break;
    case 'doubleLine':
      tempDetails.value.attributes.width = lineWidth + 'px';
      tempDetails.value.attributes.height = Number(updatedSVGStyles.height) + 'px';
      tempDetails[styleKey].width = lineWidth + 'px'; //svg width
      tempDetails[styleKey].height = Number(updatedSVGStyles.height) + 'px'; //svg height
      break;
    case 'rectangle':
      // console.log({ value: tempDetails.value });
      tempDetails.value.attributes.width = updatedSVGStyles.width + 'px';
      tempDetails.value.attributes.height = Number(updatedSVGStyles.height) + 'px';
      tempDetails.value.attributes.viewBox = `0 0 ${updatedSVGStyles.width} ${updatedSVGStyles.height}`;
      let dataBorderWidth = updatedSVGStyles.borderWidth ? updatedSVGStyles.borderWidth: parseInt(tempDetails.value.attributes['data-border-width']);
      let dataBorderRadius = updatedSVGStyles.borderRadius ? updatedSVGStyles.borderRadius: parseInt(tempDetails.value.attributes['data-border-radius']);
      if (updatedSVGStyles.scaling) {
        const scaling = updatedSVGStyles.scale ? updatedSVGStyles.scale : (Number(updatedSVGStyles.width) / parseInt(`${element.elementDetails.value.attributes.width}`.replace('px', '')));
        console.log("svg styles :: ", scaling);
        dataBorderWidth *= scaling;
        dataBorderRadius *= scaling;
      }
      const { element: updatedElement } = updateBorderRectangle(dataBorderWidth, dataBorderRadius, Object.assign({}, element, {
        elementDetails: tempDetails
      }));
      tempDetails = updatedElement.elementDetails;
      break;
    
    case 'circle':
      const { width, height } = updatedSVGStyles;
      tempDetails.value.attributes.width = `${width}px`;
      tempDetails.value.attributes.height = `${height}px`;
      
      const circleBorderWidth = tempDetails.value.children[0].attributes['stroke-width'];

      const { element: modifiedElement } = updateBorderCircle(circleBorderWidth, Object.assign({}, element, {
        elementDetails: tempDetails
      }));
      tempDetails = modifiedElement.elementDetails;

      /*tempDetails.value.children[0].children[0].children[0].attributes.d = `M 0 0 L ${lineWidth} 0 L ${lineWidth} ${lineWeight} L 0 ${lineWeight} Z M 0 0`;
      tempDetails.value.children[1].children[0].children[0].attributes.d = `M -9 0 L ${lineWidth} 0 L ${lineWidth} ${lineWeight} L -9 ${lineWeight}  Z M -9 0`;

      const dataBorderWidth = tempDetails.value.attributes['data-border-width'];
      const gTag = tempDetails.value.children[1].children[0];
      if (dataBorderWidth && parseInt(dataBorderWidth, 10) > 0 && gTag.children.length === 2) {
        let borderLine = gTag.children[gTag.children.length - 1]?.attributes?.d;
        let borderChild;
        if (borderLine && borderLine.match(PATH_Z_ATTR).length === 2) {
          borderChild = { ...gTag.children[gTag.children.length - 1] };
          borderChild.attributes.d = `M 0 0 L 0 ${lineWeight} L ${lineWidth} ${lineWeight} L ${lineWidth} 0 Z M 0 0 M ${lineWidth - dataBorderWidth} ${lineWeight - dataBorderWidth} L ${dataBorderWidth} ${lineWeight - dataBorderWidth} L ${dataBorderWidth} ${dataBorderWidth} L ${lineWidth - dataBorderWidth} ${dataBorderWidth} Z M ${lineWidth - dataBorderWidth} ${lineWeight - dataBorderWidth} `;
          tempDetails.value.children[1].children[0].children[gTag.children.length - 1].attributes.d = borderChild.attributes.d;
        }
      }*/
      break;
    default:
      break;
  }

  if (updatedSVGStyles.svgElement) {
    tempDetails.value.attributes.width = updatedSVGStyles.width + 'px';
    tempDetails.value.attributes.height = updatedSVGStyles.height + 'px';
    // tempDetails.value.attributes.style = `${tempDetails.value.attributes.style};width:${updatedSVGStyles.width + 'px'};height:${updatedSVGStyles.height + 'px'};`;
    tempDetails.value.attributes.style = tempDetails?.value?.attributes?.style?.replaceAll(WIDTH_STYLE_REGEX, `width:${updatedSVGStyles.width}px;`);
    tempDetails.value.attributes.style = tempDetails?.value?.attributes?.style?.replaceAll(HEIGHT_STYLE_REGEX, `height:${updatedSVGStyles.height}px;`);
  } else if (updatedSVGStyles.staticTableElement) {
    const isLines = ['line', 'doubleLine'].includes(tempDetails.uiType);
    tempDetails.value.attributes.width = tempDetails.uiType === 'doubleLine'? '200%' : '100%';
    if (!isLines) {
      tempDetails.value.attributes.height = '100%';
      tempDetails.value.attributes.style = tempDetails?.value?.attributes?.style?.replaceAll(
        HEIGHT_STYLE_REGEX,
        `height:100%;`,
      );
    }
    // tempDetails.value.attributes.style = `${tempDetails.value.attributes.style};width:${updatedSVGStyles.width + 'px'};height:${updatedSVGStyles.height + 'px'};`;
    tempDetails.value.attributes.style = tempDetails?.value?.attributes?.style?.replaceAll(WIDTH_STYLE_REGEX, `width: 100%;`);
  }

  tempDetails[styleKey].transform = updatedSVGStyles.transform;

  let parsedString = JSON.parse(JSON.stringify(tempDetails.value));
  const stringSVG = stringify(Object.assign({}, tempDetails.value, tempDetails.uiType === 'line' || tempDetails.uiType === 'doubleLine' ? {
    attributes: Object.assign({}, tempDetails.value.attributes, {...(updatedSVGStyles.isUrl !== undefined && !updatedSVGStyles.isUrl && { height: "auto" })})
  } : {}));
  const isUrl = updatedSVGStyles.isUrl !== undefined ? updatedSVGStyles.isUrl : (updatedSVGStyles.svgElement || updatedSVGStyles.staticTableElement);
  if (!isUrl) {
    const url = svgString2Url(stringSVG); // convert the svg string into blob url
    parsedString = { ...parsedString, url };
    // console.log({ newUrl: url });

    if (prevUrl) {
      setTimeout(() => {
        URL.revokeObjectURL(prevUrl);
      }, 10000);
    }
  }
  tempDetails.value = parsedString;

  return {
    element: Object.assign({}, element, {
      elementDetails: tempDetails,
    }),
    parsedString,
    stringSVG
  };
};

export const correctBaseUrl = (thumbnail) => {
  if (thumbnail && thumbnail.includes('BASEURL')) {
    return APP_CONSTANTS.BASE_URL + thumbnail.split('/DocStudio/')[1] + `&now=${(new Date()).getTime()}`;
  } else {
    return thumbnail;
  }
}
